import axios from 'axios';

/**
 * Interface representing a Group
 */
export interface Group {
  id: number;
  name: string;
}

/**
 * Interface for group data coming from forms
 */
export interface GroupFormData {
  name: string;
}

/**
 * Interface representing a Group Standing
 */
export interface GroupStanding {
  team_id: number;
  team_name: string;
  team_logo: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goals_for: number;
  goals_against: number;
  goal_difference: number;
  points: number;
}

/**
 * Fetches all groups from the API
 * @returns Array of groups
 */
export const getGroups = async (): Promise<Group[]> => {
  try {
    const response = await axios.get<Group[]>('/api/groups');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching groups:', error);
    return [];
  }
};

/**
 * Creates a new group
 * @param groupData - Group data containing the name
 * @returns Created group
 */
export const createGroup = async (groupData: GroupFormData): Promise<Group> => {
  try {
    const response = await axios.post<Group>('/api/groups', groupData);
    return response.data;
  } catch (error) {
    console.error('Error creating group:', error);
    throw error;
  }
};

/**
 * Updates an existing group
 * @param id - Group ID
 * @param groupData - Group data containing the name
 * @returns Updated group
 */
export const updateGroup = async (id: number, groupData: GroupFormData): Promise<Group> => {
  try {
    const response = await axios.put<Group>(`/api/groups/${id}`, groupData);
    return response.data;
  } catch (error) {
    console.error(`Error updating group ${id}:`, error);
    throw error;
  }
};

/**
 * Deletes a group
 * @param id - Group ID to delete
 * @returns Response data
 */
export const deleteGroup = async (id: number): Promise<any> => {
  try {
    const response = await axios.delete(`/api/groups/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting group ${id}:`, error);
    throw error;
  }
};

/**
 * Fetches the standings for a specific group
 * @param groupId - Group ID
 * @returns Array of group standings
 */
export const getGroupStandings = async (groupId: number): Promise<GroupStanding[]> => {
  try {
    const response = await axios.get<GroupStanding[]>(`/api/groups/${groupId}/standings`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching group standings:', error);
    return [];
  }
}; 